<template>
  <div class="wrapper-landing lbn">

    <!-- ─── HERO ─── -->
    <header class="lbn-hero" ref="heroRef">
      <div class="lbn-hero__bg" aria-hidden="true"></div>

      <div class="lbn-hero__logo">
        <router-link to="#">
          <img src="../../assets/img/logos/logo-hellowish-dark.webp" alt="HelloWish" />
        </router-link>
      </div>

      <div class="lbn-hero__content">
        <h1 class="lbn-hero__title">
          Checklist: <em>todo</em> lo que tu bebé necesitará
        </h1>
        <p class="lbn-hero__subtitle">
          Descubre lo que más de 100.000 mamás suelen poner en sus listas de nacimiento con HelloWish
        </p>
        <button class="lbn-btn-scroll" @click="scrollToList">
          Ver checklist
          <span class="lbn-btn-scroll__arrow" aria-hidden="true">↓</span>
        </button>
      </div>

      <div class="lbn-hero__cover lbn-hero__cover--bleed">
        <img
          class="lbn-hero__img"
          src="../../assets/img/covers/landing-edu.webp"
          alt=""
          loading="eager"
        />
      </div>
    </header>

    <!-- ─── SECCIÓN LISTA ─── -->
    <section class="lbn-section" ref="listaSection">
      <div class="lbn-lista-wrap">

        <h2 class="lbn-section-title">Un checklist para tu bebé usando HelloWish</h2>

        <!-- Cabecera de lista -->
        <div class="lbn-list-header">
          <img v-if="ownerPhoto" class="lbn-list-avatar" :src="ownerPhoto" alt="" />
          <div>
            <h2 class="lbn-list-header-name">{{ listTitle }}</h2>
            <p v-if="welcomeMessage" class="lbn-list-header-welcome">{{ welcomeMessage }}</p>
          </div>
        </div>

        <!-- Productos agrupados por categoría (mostramos solo una parte, el resto se envía por email) -->
        <div class="lbn-products-fade">
        <div v-for="group in visibleProductosByCategory" :key="group.category" class="lbn-category-group">
          <h3 v-if="group.category" class="lbn-category-title">{{ group.category }}</h3>
          <div class="lbn-product-list">
          <article
            v-for="(p, i) in group.items"
            :key="i"
            class="lbn-card"
          >
            <!-- Imagen -->
            <section class="lbn-card__left" v-if="p.photo">
              <div class="lbn-card__img-wrap">
                <img
                  class="lbn-card__img"
                  :src="p.photo"
                  :alt="p.name"
                  loading="lazy"
                />
              </div>
            </section>

            <!-- Contenido -->
            <aside class="lbn-card__right">
              <!-- Imprescindible -->
              <div class="lbn-card__musthave" v-if="p.mustHave">
                <img src="../../assets/img/ico/ico-heart-fill.svg" alt="" width="14" height="14" />
                Sandra lo ha marcado como imprescindible
              </div>

              <!-- Título -->
              <h3 class="lbn-card__title">{{ p.name }}</h3>

              <!-- Comentario / apunte -->
              <div class="lbn-card__comments" v-if="p.note">
                <p class="lbn-card__comments-label">HelloWish ha dejado un comentario:</p>
                <p class="lbn-card__comments-text">{{ p.note }}</p>
              </div>

              <!-- Precio -->
              <div class="lbn-card__price-row" v-if="p.price">
                <span class="lbn-card__price-label">Presupuesto estimado</span>
                <span class="lbn-card__price-val">{{ p.price }} €</span>
              </div>

              <!-- Retailer + CTA -->
              <div class="lbn-card__footer">
                <p class="lbn-card__retailers-label" v-if="p.retailerLogo">Añadido a la lista desde:</p>
                <div class="lbn-card__footer-row">
                  <div class="lbn-card__retailers-list" v-if="p.retailerLogo">
                    <span class="lbn-retailer-chip">
                      <img class="lbn-retailer-chip__logo" :src="p.retailerLogo" alt="" />
                    </span>
                  </div>
                  <!-- CTA deshabilitado: ya no debe llevar a la pantalla de registro
                  <button class="lbn-card__copy-btn" @click="onCopyList">
                    <i class="uil uil-list-ul" aria-hidden="true"></i>
                    Cópialo a tu lista
                  </button>
                  -->
                </div>
              </div>
            </aside>
          </article>
          </div>
        </div>

        <!-- Siluetas de los siguientes productos: desenfocadas a propósito -->
        <div class="lbn-teaser-row" v-if="teaserProductos.length">
          <div v-for="(p, i) in teaserProductos" :key="i" class="lbn-teaser-card">
            <img v-if="p.photo" :src="p.photo" alt="" class="lbn-teaser-card__img" />
          </div>
        </div>

        <!-- Mensaje de bloqueo: corto y explícito, conecta con el formulario -->
        <div class="lbn-list-lock">
          <p class="lbn-list-lock__title">¿Quieres ver el resto de la lista?</p>
          <p class="lbn-list-lock__sub">Recibe el checklist completo con todos los imprescindibles.</p>
        </div>
        </div>

      </div>
    </section>

    <!-- ─── STICKY CTA ─── -->
    <div class="lbn-sticky" :class="{ 'lbn-sticky--minimized': stickyMinimized }" v-show="showStickyCta" role="complementary">
      <button
        v-if="stickyMinimized"
        type="button"
        class="lbn-sticky__collapsed"
        @click="stickyMinimized = false"
      >
        <span>Envía el checklist completo a tu email</span>
        <svg class="lbn-sticky__collapsed-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      </button>

      <button
        v-if="!stickyMinimized"
        type="button"
        class="lbn-sticky__minimize"
        @click="stickyMinimized = true"
        aria-label="Minimizar"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="4 14 10 14 10 20"></polyline>
          <polyline points="20 10 14 10 14 4"></polyline>
          <line x1="14" y1="10" x2="21" y2="3"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      </button>

      <div v-show="!stickyMinimized" class="lbn-sticky__inner">
        <p class="lbn-sticky__title">Envía el checklist completo a tu email</p>

        <div class="lbn-sticky__field">
          <input
            type="email"
            class="lbn-sticky__input"
            :class="{ 'lbn-sticky__input--error': stickyForm.emailError }"
            placeholder="tu@email.com"
            autocomplete="email"
            v-model.trim="stickyForm.email"
            @input="stickyForm.emailError = ''"
          />
          <p v-if="stickyForm.emailError" class="lbn-sticky__field-error">{{ stickyForm.emailError }}</p>
        </div>

        <div class="lbn-sticky__consent-wrap">
          <label class="lbn-sticky__consent">
            <input type="checkbox" v-model="stickyForm.consent" @change="stickyForm.consentError = ''" />
            <span>Acepto la <router-link :to="$t('routes.confidentialityPolicy', 'es-ES')" target="_blank">política de datos</router-link>.</span>
          </label>
          <p v-if="stickyForm.consentError" class="lbn-sticky__field-error">{{ stickyForm.consentError }}</p>
        </div>

        <button
          type="button"
          class="lbn-sticky__btn"
          :disabled="stickyForm.sending || stickyForm.sent"
          @click="onSendChecklistSticky"
        >
          {{ stickyForm.sending ? 'Enviando…' : 'Enviar' }}
        </button>

        <p v-if="stickyForm.sent" class="lbn-sticky__feedback lbn-sticky__feedback--ok">¡Enviado! Revisa tu email.</p>
        <p v-else-if="stickyForm.error" class="lbn-sticky__feedback lbn-sticky__feedback--error">{{ stickyForm.error }}</p>
      </div>
    </div>

    <!-- ─── CIERRE ─── -->
    <section class="lbn-cierre" ref="cierreSection">
      <div class="lbn-lista-wrap lbn-lista-wrap--center">
        <div class="lbn-final">
          <h2 class="lbn-final__title">Recibe el checklist completo</h2>
          <p class="lbn-final__sub">Te lo enviaremos por email y, si quieres, también a tu pareja.</p>

          <div class="lbn-final__field">
            <label class="lbn-final__label" for="lbn-final-email">
              Tu email <span class="lbn-final__required">*</span>
            </label>
            <input
              id="lbn-final-email"
              type="email"
              class="lbn-final__input"
              :class="{ 'lbn-final__input--error': finalForm.emailError }"
              placeholder="tu@email.com"
              autocomplete="email"
              v-model.trim="finalForm.email"
              @input="finalForm.emailError = ''"
            />
            <p v-if="finalForm.emailError" class="lbn-final__field-error">{{ finalForm.emailError }}</p>
          </div>

          <div class="lbn-final__field">
            <label class="lbn-final__label" for="lbn-final-partner-email">
              Email de tu pareja <span class="lbn-final__optional">(opcional)</span>
            </label>
            <input
              id="lbn-final-partner-email"
              type="email"
              class="lbn-final__input"
              :class="{ 'lbn-final__input--error': finalForm.partnerEmailError }"
              placeholder="cariño@email.com"
              autocomplete="email"
              v-model.trim="finalForm.partnerEmail"
              @input="finalForm.partnerEmailError = ''"
            />
            <p v-if="finalForm.partnerEmailError" class="lbn-final__field-error">{{ finalForm.partnerEmailError }}</p>
          </div>

          <label class="lbn-final__consent">
            <input type="checkbox" v-model="finalForm.consent" @change="finalForm.consentError = ''" />
            <span>Acepto la <router-link :to="$t('routes.confidentialityPolicy', 'es-ES')" target="_blank">política de datos</router-link>.</span>
          </label>
          <p v-if="finalForm.consentError" class="lbn-final__field-error">{{ finalForm.consentError }}</p>

          <button
            type="button"
            class="lbn-btn-final"
            :disabled="finalForm.sending || finalForm.sent"
            @click="onSendChecklistFinal"
          >
            {{ finalForm.sending ? 'Enviando…' : 'Enviar checklist' }}
          </button>
          <p v-if="finalForm.sent" class="lbn-final__feedback lbn-final__feedback--ok">¡Enviado! Revisa tu email.</p>
          <p v-else-if="finalForm.error" class="lbn-final__feedback lbn-final__feedback--error">{{ finalForm.error }}</p>
        </div>
      </div>
    </section>

    <LandingFooter />
  </div>
</template>

<script>
import LandingFooter from "./components/LandingFooter.vue";
import axios from "axios";

const IMG = "https://images.hellobb.net/";
const LOGO_IMG = "https://images.hellobb.net/logos/";

export default {
  name: "LandingBabyNeeds",
  components: { LandingFooter },
  metaInfo() {
    return {
      title: "¿Qué necesita un bebé? Lista completa de nacimiento — HelloWish",
      meta: [
        {
          name: "description",
          content:
            "Descubre la lista completa de todo lo que necesita un bebé. Más de 100.000 mamás ya la usan como guía con HelloWish.",
        },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  data() {
    return {
      heroVisible: true,
      cierreVisible: false,
      ioHero: null,
      ioCierre: null,
      ownerPhoto: null,
      listTitle: '',
      welcomeMessage: '',
      productos: [],
      scroll75Fired: false,
      listId: '',
      stickyMinimized: true,
      finalForm: {
        email: '', partnerEmail: '', consent: false,
        sending: false, sent: false, error: '',
        emailError: '', partnerEmailError: '', consentError: '',
      },
      stickyForm: {
        email: '', consent: false,
        sending: false, sent: false, error: '',
        emailError: '', consentError: '',
      },
    };
  },
  computed: {
    showStickyCta() {
      return !this.heroVisible && !this.cierreVisible;
    },
    guestChecklistUrl() {
      if (!this.listId) return '';
      const base = this.$utils.removeDoubleSlashes(`${process.env.APP_URL}/guest/${this.listId}`);
      return `${base}?utm_source=paid&utm_medium=email&utm_campaign=demandgen&utm_term=checklist`;
    },
    productosByCategory() {
      const ORDER = [
        'de paseo',
        'a comer',
        'en el coche',
        'hogar',
        'a jugar',
        'baño y cuidados',
        'para mamá',
        'ropa',
      ];
      const map = new Map();
      for (const p of this.productos) {
        const cat = p.category || '';
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(p);
      }
      return Array.from(map.entries())
        .map(([category, items]) => ({ category, items }))
        .sort((a, b) => {
          const ia = ORDER.indexOf(a.category.toLowerCase());
          const ib = ORDER.indexOf(b.category.toLowerCase());
          const pa = ia === -1 ? Infinity : ia;
          const pb = ib === -1 ? Infinity : ib;
          return pa - pb;
        });
    },
    orderedProductos() {
      return this.productosByCategory.flatMap(g => g.items);
    },
    visibleProductosByCategory() {
      const total = this.orderedProductos.length;
      if (!total) return [];
      const limit = Math.max(1, Math.round(total * 0.6));
      const result = [];
      let count = 0;
      for (const group of this.productosByCategory) {
        if (count >= limit) break;
        const items = group.items.slice(0, limit - count);
        result.push({ category: group.category, items });
        count += items.length;
      }
      return result;
    },
    teaserProductos() {
      const total = this.orderedProductos.length;
      if (!total) return [];
      const limit = Math.max(1, Math.round(total * 0.6));
      return this.orderedProductos.slice(limit, limit + 2);
    },
  },
  async mounted() {
    const heroEl = this.$refs.heroRef;
    if (heroEl) {
      this.ioHero = new IntersectionObserver(
        ([entry]) => { this.heroVisible = entry.isIntersecting; },
        { threshold: 0 }
      );
      this.ioHero.observe(heroEl);
    }
    const cierreEl = this.$refs.cierreSection;
    if (cierreEl) {
      this.ioCierre = new IntersectionObserver(
        ([entry]) => { this.cierreVisible = entry.isIntersecting; },
        { threshold: 0 }
      );
      this.ioCierre.observe(cierreEl);
    }

    window.addEventListener('scroll', this.onCheckScroll75, { passive: true });
    window.addEventListener('resize', this.onCheckStickyDesktop);
    this.onCheckStickyDesktop();

    try {
      const isProd = process.env.API === 'https://hellobb-api.azurewebsites.net/api';
      const listId = isProd ? 'ERnM' : 'lxA6';
      this.listId = listId;
      const { data } = await axios.get(`${process.env.API}/list/minimal/${listId}`);
      const USER_IMG = process.env.URL_IMG_USER;
      this.ownerPhoto = data.photo ? `${USER_IMG}${data.photo.replace(USER_IMG, '')}` : null;
      this.listTitle = data.title || '';
      this.welcomeMessage = data.welcomeMessage || '';
      this.productos = data.products.map(p => ({
        category:     p.mainCategory || '',
        name:         p.title,
        photo:        p.photo ? `${IMG}${p.photo.replace(IMG, '')}` : null,
        note:         p.info || null,
        mustHave:     p.mustHave,
        price:        p.price != null ? new Intl.NumberFormat('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(p.price)) : null,
        retailerLogo: p.retailerLogo ? `${LOGO_IMG}${p.retailerLogo.replace(LOGO_IMG, '')}?tr=w-88,h-88,cm-pad_resize` : null,
      }));
    } catch (e) {
      // fail silently, la lista queda vacía
    }
  },
  beforeDestroy() {
    if (this.ioHero)   { this.ioHero.disconnect();   this.ioHero   = null; }
    if (this.ioCierre) { this.ioCierre.disconnect(); this.ioCierre = null; }
    window.removeEventListener('scroll', this.onCheckScroll75);
    window.removeEventListener('resize', this.onCheckStickyDesktop);
  },
  methods: {
    onCheckStickyDesktop() {
      if (window.innerWidth >= 1024 && this.stickyMinimized) {
        this.stickyMinimized = false;
      }
    },
    onCheckScroll75() {
      if (this.scroll75Fired) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total    = document.body.scrollHeight;
      if (total > 0 && scrolled / total >= 0.75) {
        this.scroll75Fired = true;
        window.removeEventListener('scroll', this.onCheckScroll75);
        console.log('[LBN] ScrollDepth75 — disparando evento');
        this.fireScroll75Event();
      }
    },
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    },
    fireScroll75Event() {
      this.fireMetaEvent('LandingScroll');
    },
    fireMetaEvent(eventName, extra = {}) {
      try {
        const eventId   = `${eventName}_${Date.now()}_${crypto.randomUUID()}`;
        const ckyConsent = window.getCkyConsent?.();
        const hasConsent = !!(ckyConsent?.categories?.advertisement);
        const fbclid = new URLSearchParams(window.location.search).get('fbclid');

        const payload = {
          eventName,
          cookieConsent:  hasConsent,
          fbp:            hasConsent ? this.getCookie('_fbp') : null,
          fbc:            hasConsent ? this.getCookie('_fbc') : null,
          eventSourceUrl: window.location.href,
          eventId,
          ...extra,
        };
        if (fbclid) payload.fbclid = fbclid;

        fetch(`${process.env.API}/meta/event`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        })
          .then(r => console.log(`[LBN] ${eventName} → ${r.status}`, payload))
          .catch(err => console.warn(`[LBN] ${eventName} fetch error`, err));
      } catch (_) {
        // fail silently
      }
    },
    scrollToList() {
      const el = this.$refs.listaSection;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.pageYOffset - 16;
      window.scrollTo({ top, behavior: "smooth" });
    },
    onCopyList() {
      this.$router.push({ name: "landing-signup-baby-needs" });
    },
    validateStickyForm() {
      this.stickyForm.emailError = '';
      this.stickyForm.consentError = '';
      if (!this.stickyForm.email) {
        this.stickyForm.emailError = 'Introduce tu email.';
      } else if (!this.$utils.validateEmail(this.stickyForm.email)) {
        this.stickyForm.emailError = 'Este email no es válido.';
      }
      if (!this.stickyForm.consent) {
        this.stickyForm.consentError = 'Debes aceptar la política de datos.';
      }
      return !this.stickyForm.emailError && !this.stickyForm.consentError;
    },
    validateFinalForm() {
      this.finalForm.emailError = '';
      this.finalForm.partnerEmailError = '';
      this.finalForm.consentError = '';
      if (!this.finalForm.email) {
        this.finalForm.emailError = 'Introduce tu email.';
      } else if (!this.$utils.validateEmail(this.finalForm.email)) {
        this.finalForm.emailError = 'Este email no es válido.';
      }
      if (this.finalForm.partnerEmail && !this.$utils.validateEmail(this.finalForm.partnerEmail)) {
        this.finalForm.partnerEmailError = 'Este email no es válido.';
      }
      if (!this.finalForm.consent) {
        this.finalForm.consentError = 'Debes aceptar la política de datos.';
      }
      return !this.finalForm.emailError && !this.finalForm.partnerEmailError && !this.finalForm.consentError;
    },
    async onSendChecklistSticky() {
      if (this.stickyForm.sending || this.stickyForm.sent) return;
      this.stickyForm.error = '';
      if (!this.validateStickyForm()) return;
      this.stickyForm.sending = true;
      try {
        await this.$store.dispatch('sendChecklistByEmail', {
          to: this.stickyForm.email,
          url: this.guestChecklistUrl,
        });
        this.fireMetaEvent('LandingChecklistEmail', { email: this.stickyForm.email });
        this.stickyForm.sent = true;
      } catch (e) {
        this.stickyForm.error = 'No hemos podido enviar el checklist. Inténtalo de nuevo.';
      } finally {
        this.stickyForm.sending = false;
      }
    },
    async onSendChecklistFinal() {
      if (this.finalForm.sending || this.finalForm.sent) return;
      this.finalForm.error = '';
      if (!this.validateFinalForm()) return;
      this.finalForm.sending = true;
      try {
        const emails = [this.finalForm.email];
        if (this.finalForm.partnerEmail) emails.push(this.finalForm.partnerEmail);

        await Promise.all(emails.map(to => this.$store.dispatch('sendChecklistByEmail', {
          to,
          url: this.guestChecklistUrl,
        })));
        emails.forEach(email => this.fireMetaEvent('LandingChecklistEmail', { email }));
        this.finalForm.sent = true;
      } catch (e) {
        this.finalForm.error = 'No hemos podido enviar el checklist. Inténtalo de nuevo.';
      } finally {
        this.finalForm.sending = false;
      }
    },
  },
};
</script>

<style>
/* ── HERO ────────────────────────────────────────────────── */
.lbn-hero {
  background: #FFCC00;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 32px 24px 56px;
}
.lbn-hero__bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
.lbn-hero__bg::before,
.lbn-hero__bg::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.07);
}
.lbn-hero__bg::before { width: 360px; height: 360px; top: -120px; right: -100px; }
.lbn-hero__bg::after  { width: 280px; height: 280px; bottom: -100px; left: -80px; }

.lbn-hero__logo {
  align-self: flex-start;
  flex-shrink: 0;
  margin-bottom: 32px;
  z-index: 1;
  position: relative;
}
.lbn-hero__logo img { width: 120px; height: auto; display: block; }

.lbn-hero__content {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  text-align: center;
}
.lbn-hero__title {
  font-size: clamp(32px, 8vw, 52px);
  font-weight: 700;
  color: #312A5E;
  line-height: 1.15;
  letter-spacing: -1.5px;
  margin-bottom: 16px;
}
.lbn-hero__title em { font-style: normal; color: #fff; }
.lbn-hero__subtitle {
  font-size: 17px;
  color: #312A5E;
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 32px;
}

.lbn-btn-scroll {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #5d50c6;
  color: #fff;
  font-family: inherit;
  font-size: 17px;
  font-weight: 800;
  padding: 18px 36px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  letter-spacing: -0.2px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 4px 20px rgba(93, 80, 198, 0.35);
  align-self: center;
}
.lbn-btn-scroll:hover  { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(93, 80, 198, 0.45); background: #473d99; }
.lbn-btn-scroll:active { transform: translateY(0); }
.lbn-btn-scroll__arrow {
  font-size: 20px;
  line-height: 1;
  animation: lbn-bounce 1.8s ease infinite;
}
@keyframes lbn-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(5px); }
}

.lbn-hero__cover {
  width: 100%;
  margin-top: 40px;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  z-index: 1;
  min-height: 200px;
}
.lbn-hero__cover--bleed {
  width: 100vw;
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: -56px;
}
.lbn-hero__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
  mix-blend-mode: multiply;
  animation: lbn-fadein 0.6s 0.3s ease both;
}
@keyframes lbn-fadein {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (min-width: 1024px) {
  .lbn-hero {
    flex-direction: row;
    align-items: center;
    gap: 64px;
    padding: 80px 10%;
    min-height: 100svh;
  }
  .lbn-hero__logo { align-self: flex-start; margin-bottom: 0; }
  .lbn-hero__logo img { width: 140px; }
  .lbn-hero__content { flex: 1; max-width: 480px; text-align: left; }
  .lbn-btn-scroll { align-self: flex-start; }
  .lbn-hero__cover,
  .lbn-hero__cover--bleed { flex-shrink: 0; width: auto; margin: 0; }
  .lbn-hero__img {
    width: clamp(320px, 28vw, 440px);
    -webkit-mask-image:
      linear-gradient(to right, transparent 0%, black 3%, black 97%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black 2%, black 97%, transparent 100%);
    mask-image:
      linear-gradient(to right, transparent 0%, black 3%, black 97%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black 2%, black 97%, transparent 100%);
    -webkit-mask-composite: source-in;
    mask-composite: intersect;
  }
}

/* ── Sección lista ───────────────────────────────────────── */
.lbn-section {
  padding: 64px 24px 8px;
  background: #faf8f7;
}
@media (min-width: 768px) { .lbn-section { padding: 64px 24px 16px; } }
.lbn-lista-wrap {
  max-width: 760px;
  margin: 0 auto;
}
.lbn-lista-wrap--center { text-align: center; }

/* ── Cabecera de lista ───────────────────────────────────── */
.lbn-list-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;
}
.lbn-list-avatar {
  width: 90px;
  height: 90px;
  border-radius: 60px;
  object-fit: cover;
  object-position: top;
  border: 4px solid #fff;
  box-shadow: 0 2px 8px rgba(30, 0, 40, 0.15);
  flex-shrink: 0;
}
@media (min-width: 640px) {
  .lbn-list-avatar { width: 108px; height: 108px; }
}
.lbn-list-header-name {
  font-size: clamp(1.25rem, 4vw, 1.875rem);
  font-weight: 600;
  color: #1e0028;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
}
.lbn-list-header-meta {
  font-size: 0.875rem;
  color: #7f7f7f;
  margin: 0;
}
.lbn-list-header-welcome {
  font-size: 0.875rem;
  color: #4b4b4b;
  margin: 4px 0 0;
  line-height: 1.5;
}

/* ── Título de sección ───────────────────────────────────── */
.lbn-section-title {
  font-size: clamp(24px, 5vw, 36px);
  font-weight: 800;
  color: #312A5E;
  line-height: 1.2;
  margin: 0 0 32px;
  letter-spacing: -0.8px;
}

/* ── Grupos de categoría ─────────────────────────────────── */
.lbn-category-group {
  margin-bottom: 2.5rem;
}
.lbn-category-title {
  font-size: clamp(15px, 3vw, 18px);
  font-weight: 700;
  color: #312A5E;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #FFCC00;
  display: inline-block;
}

/* ── Lista de cards ──────────────────────────────────────── */
.lbn-product-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
@media (min-width: 768px) {
  .lbn-product-list { gap: 2.5rem; }
}

/* ── Card ────────────────────────────────────────────────── */
.lbn-card {
  background: #fff;
  box-shadow: 0px 8px 16px rgba(30, 0, 40, 0.08);
  border-radius: 0.625rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.75rem;
  transition: box-shadow 0.1s ease-in-out;
}
.lbn-card:hover { box-shadow: 0px 16px 48px rgba(30, 0, 40, 0.1); }
@media (min-width: 768px) {
  .lbn-card { padding: 1.75rem 1.5rem 1.25rem; }
}

/* ── Tramo final de la lista: siluetas desenfocadas + mensaje explícito ── */
.lbn-products-fade {
  position: relative;
}

/* Última categoría sin margen extra: el propio bloque de bloqueo ya aporta espacio */
.lbn-products-fade .lbn-category-group:last-child {
  margin-bottom: 0;
}

/* Fila de 1-2 productos siguientes: solo silueta, desenfoque explícito (no un blur ambiguo) */
.lbn-teaser-row {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}
.lbn-teaser-card {
  flex: 1;
  max-width: 220px;
  aspect-ratio: 1 / 1;
  background: #fff;
  border: 1px solid #d7d7d7;
  border-radius: 0.625rem;
  box-shadow: 0px 8px 16px rgba(30, 0, 40, 0.08);
  overflow: hidden;
  filter: blur(5px);
  opacity: 0.7;
}
.lbn-teaser-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 14px;
  box-sizing: border-box;
  display: block;
}

/* Mensaje de bloqueo: corto, explícito, se superpone ligeramente a la fila de siluetas */
.lbn-list-lock {
  position: relative;
  margin-top: -56px;
  padding: 72px 16px 0;
  text-align: center;
  background: linear-gradient(to bottom, rgba(250, 248, 247, 0) 0%, #faf8f7 65%);
}
.lbn-list-lock__title {
  font-size: 1.1875rem;
  font-weight: 800;
  color: #312A5E;
  margin: 0 0 6px;
  letter-spacing: -0.3px;
}
.lbn-list-lock__sub {
  font-size: 0.9375rem;
  color: #4b4b4b;
  margin: 0;
  line-height: 1.5;
}

/* ── Columna imagen ──────────────────────────────────────── */
.lbn-card__left {
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
@media (min-width: 560px) { .lbn-card__left { flex: 0 0 200px; } }
@media (min-width: 768px) { .lbn-card__left { flex: 0 0 232px; } }

.lbn-card__img-wrap {
  aspect-ratio: 26 / 17;
  border: 1px solid #d7d7d7;
  border-radius: 0.625rem;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: opacity 0.2s;
  cursor: pointer;
}
.lbn-card__img-wrap--dim { opacity: 0.4; }
@media (min-width: 560px) { .lbn-card__img-wrap { aspect-ratio: 1 / 1; } }

.lbn-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: 10px;
  box-sizing: border-box;
}

.lbn-card__status-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

/* ── Badge ───────────────────────────────────────────────── */
.lbn-badge {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.lbn-badge--accent  { background: #5d50c6; color: #fff; }
.lbn-badge--success { background: #28a745; color: #fff; }

/* ── Botón reservar ──────────────────────────────────────── */
.lbn-btn-reserve {
  width: 100%;
  background: none;
  border: 1.5px solid #d7d7d7;
  border-radius: 8px;
  padding: 10px 16px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #1e0028;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: border-color 0.15s, background 0.15s;
}
.lbn-btn-reserve:hover  { border-color: #5d50c6; background: #f5f4ff; }
.lbn-btn-reserve--desk  { display: none; }
.lbn-btn-reserve--mob   { display: flex; }
@media (min-width: 560px) {
  .lbn-btn-reserve--desk { display: flex; }
  .lbn-btn-reserve--mob  { display: none; }
}

/* ── Columna contenido ───────────────────────────────────── */
.lbn-card__right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lbn-card__musthave {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e0028;
}

.lbn-card__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e0028;
  margin: 0;
  line-height: 1.4;
}

.lbn-card__comments {
  background: #f5f5f5;
  border-radius: 0.625rem;
  padding: 0.875rem;
}
.lbn-card__comments-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e0028;
  margin: 0 0 4px;
}
.lbn-card__comments-text {
  font-size: 0.875rem;
  color: #4b4b4b;
  margin: 0;
  line-height: 1.5;
}

.lbn-card__price-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #d7d7d7;
  padding-bottom: 1rem;
}
.lbn-card__price-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #7f7f7f;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.lbn-card__price-val { font-size: 1.625rem; font-weight: 600; color: #1e0028; letter-spacing: -0.5px; }

.lbn-card__retailers-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1e0028;
  margin: 0 0 10px;
}
.lbn-card__retailers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.lbn-retailer-chip {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #d7d7d7;
  background: #fff;
  flex-shrink: 0;
  overflow: hidden;
  display: inline-flex;
}
.lbn-retailer-chip__logo {
  width: 100%;
  height: 100%;
  display: block;
}
.lbn-card__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
}
.lbn-card__footer-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.lbn-card__copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0eeff;
  color: #5d50c6;
  border: 1px solid rgba(93, 80, 198, 0.18);
  border-radius: 50px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
  transition: background 0.15s ease;
}
.lbn-card__copy-btn:hover { background: #e4deff; }
.lbn-card__copy-btn .uil { font-size: 15px; line-height: 1; }

.lbn-retailer-chip__name {
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  color: #1e0028;
}

/* ── Sticky CTA ──────────────────────────────────────────── */
.lbn-sticky {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 1000;
  background: #312A5E;
  border-top: 2px solid #FFCC00;
  padding: 20px 20px 24px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 -4px 24px rgba(49, 42, 94, 0.3);
  animation: lbn-slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.lbn-sticky--minimized {
  padding: 0;
  border-top: none;
  max-height: none;
  overflow: visible;
}
@keyframes lbn-slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
.lbn-sticky__collapsed {
  width: 100%;
  background: #312A5E;
  border: none;
  border-top: 2px solid #FFCC00;
  padding: 14px 20px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.lbn-sticky__collapsed:hover { background: #3a3270; }
.lbn-sticky__collapsed-icon { width: 16px; height: 16px; color: #FFCC00; flex-shrink: 0; }
.lbn-sticky__minimize {
  position: absolute;
  top: 8px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.lbn-sticky__minimize svg { width: 14px; height: 14px; }
.lbn-sticky__minimize:hover { background: rgba(255, 255, 255, 0.22); }
.lbn-sticky__inner {
  max-width: 420px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lbn-sticky__title {
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  text-align: center;
  margin: 0;
}
.lbn-sticky__input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 50px;
  padding: 14px 20px;
  font-family: inherit;
  font-size: 15px;
  color: #1e0028;
  background: #fff;
}
.lbn-sticky__input::placeholder { color: #9a97ab; }
.lbn-sticky__input:focus { outline: 2px solid #FFCC00; outline-offset: 2px; }
.lbn-sticky__input--error { box-shadow: 0 0 0 2px #ff5c5c inset; }
.lbn-sticky__field-error {
  font-size: 12px;
  color: #ff9e9e;
  margin: 6px 0 0;
}

.lbn-sticky__consent-wrap { text-align: left; }
.lbn-sticky__consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
}
.lbn-sticky__consent input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 1px 0 0;
  flex-shrink: 0;
  accent-color: #FFCC00;
}
.lbn-sticky__consent a { color: #FFCC00; text-decoration: underline; }

.lbn-sticky__btn {
  width: 100%;
  background: #FFCC00;
  border: 1px solid #FFCC00;
  border-radius: 50px;
  padding: 14px 24px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  color: #312A5E;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: background 0.2s ease, transform 0.15s ease, opacity 0.15s ease;
}
.lbn-sticky__btn:hover:not(:disabled)  { background: #f0c000; }
.lbn-sticky__btn:active:not(:disabled) { transform: scale(0.98); }
.lbn-sticky__btn:disabled { cursor: not-allowed; }
.lbn-sticky__feedback {
  font-size: 12px;
  text-align: center;
  margin: 0;
}
.lbn-sticky__feedback--ok { color: #7be08f; }
.lbn-sticky__feedback--error { color: #ff9e9e; }
@media (min-width: 640px) {
  .lbn-sticky__btn { width: auto; align-self: center; padding: 14px 48px; }
}
@media (min-width: 1024px) {
  .lbn-sticky__inner {
    max-width: 1000px;
    display: grid;
    grid-template-columns: minmax(240px, auto) minmax(200px, 1fr) minmax(170px, auto) auto;
    align-items: center;
    gap: 20px;
  }
  .lbn-sticky__title { text-align: left; }
  .lbn-sticky__btn { width: auto; align-self: center; padding: 12px 32px; }
  .lbn-sticky__feedback { grid-column: 1 / -1; text-align: right; }
  /* El minimizado es solo un mecanismo responsive/mobile */
  .lbn-sticky__minimize,
  .lbn-sticky__collapsed { display: none; }
}

/* ── Cierre ──────────────────────────────────────────────── */
.lbn-cierre {
  padding: 20px 16px 56px;
  background: #fff;
  margin-top: -16px;
  position: relative;
}
@media (min-width: 768px) { .lbn-cierre { padding: 32px 48px 96px; } }

.lbn-final {
  background: #312A5E;
  border-radius: 32px;
  padding: 40px 20px;
  text-align: center;
}
@media (min-width: 768px) { .lbn-final { padding: 56px 40px; } }
.lbn-final__title {
  font-size: clamp(22px, 4vw, 30px);
  font-weight: 800;
  color: #fff;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  line-height: 1.3;
}
.lbn-final__sub {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  line-height: 1.6;
}

.lbn-final__field {
  max-width: 420px;
  margin: 0 auto 16px;
  text-align: left;
}
.lbn-final__label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
}
.lbn-final__required { color: #FFCC00; }
.lbn-final__optional { font-weight: 400; color: rgba(255, 255, 255, 0.5); }
.lbn-final__input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-radius: 50px;
  padding: 14px 20px;
  font-family: inherit;
  font-size: 15px;
  color: #1e0028;
  background: #fff;
}
.lbn-final__input::placeholder { color: #9a97ab; }
.lbn-final__input:focus { outline: 2px solid #FFCC00; outline-offset: 2px; }
.lbn-final__input--error { box-shadow: 0 0 0 2px #ff5c5c inset; }
.lbn-final__field-error {
  max-width: 420px;
  margin: 6px auto 0;
  font-size: 12px;
  color: #ff9e9e;
  text-align: left;
}

.lbn-final__consent {
  max-width: 420px;
  margin: 4px auto 24px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
  cursor: pointer;
}
.lbn-final__consent input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 1px 0 0;
  flex-shrink: 0;
  accent-color: #FFCC00;
}
.lbn-final__consent a { color: #FFCC00; text-decoration: underline; }

.lbn-btn-final {
  display: inline-block;
  background: #FFCC00;
  color: #312A5E;
  font-family: inherit;
  font-size: 17px;
  font-weight: 800;
  padding: 18px 48px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  letter-spacing: -0.2px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  box-shadow: 0 4px 20px rgba(255, 204, 0, 0.35);
}
.lbn-btn-final:hover:not(:disabled)  { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(255, 204, 0, 0.4); }
.lbn-btn-final:active:not(:disabled) { transform: translateY(0); }
.lbn-btn-final:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
.lbn-final__feedback {
  font-size: 13px;
  margin: 16px 0 0;
}
.lbn-final__feedback--ok { color: #7be08f; }
.lbn-final__feedback--error { color: #ff9e9e; }
.lbn-final__reinforce {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 12px;
  margin-bottom: 0;
}
</style>
